/**
 * RapierVehicle - Rapier-specific implementation of IPhysicsVehicle. Wraps
 * DynamicRayCastVehicleController to provide vehicle physics with wheel simulation,
 * suspension, and steering capabilities.
 */

import * as THREE from 'three';

import type { IPhysicsVehicle } from '../IPhysicsVehicle.js';
import type { VehicleState, VehicleWheelConfig, VehicleWheelState } from '../IPhysicsVehicle.js';
import type * as RAPIER from '@dimforge/rapier3d-compat/rapier.es.js';

export interface RapierVehicleOptions {
  world: RAPIER.World;
  chassis: RAPIER.RigidBody;
}

export class RapierVehicle implements IPhysicsVehicle {
  private vehicleController: RAPIER.DynamicRayCastVehicleController | null = null;
  private wheelConfigs: Map<number, VehicleWheelConfig> = new Map();
  private wheelRotationAngles: Map<number, number> = new Map();
  private lastTime: number = 0;

  static get DEFAULT_DIRECTION(): RAPIER.Vector {
    return { x: 0, y: -1, z: 0 };
  }

  static get DEFAULT_AXLE(): RAPIER.Vector {
    return { x: -1, y: 0, z: 0 };
  }

  static get ZERO_VECTOR(): RAPIER.Vector {
    return { x: 0, y: 0, z: 0 };
  }

  constructor(private options: RapierVehicleOptions) {
    this.vehicleController = this.options.world.createVehicleController(this.options.chassis);
  }

  public destroy(): void {
    if (this.vehicleController) {
      this.options.world.removeVehicleController(this.vehicleController);
      this.vehicleController = null;
    }
    this.wheelConfigs.clear();
    this.wheelRotationAngles.clear();
  }

  public tick(deltaTime: number): void {
    // clamp the update time to 1/60 to make the simulation more stable
    if (this.vehicleController) {
      const chassis = this.vehicleController.chassis();
      if (chassis.isSleeping()) {
        chassis.wakeUp();
      }
      this.vehicleController.updateVehicle(Math.min(deltaTime, 1/60));

      // Update wheel rotation based on chassis linear velocity
      this.updateWheelRotations(deltaTime);
    }
  }

  private updateWheelRotations(deltaTime: number): void {
    if (!this.vehicleController) return;

    const chassis = this.vehicleController.chassis();
    const velocity = chassis.linvel();
    const velocityVector = new THREE.Vector3(velocity.x, velocity.y, velocity.z);

    // Get the chassis forward direction (assuming Z is forward)
    const chassisRotation = chassis.rotation();
    const chassisQuaternion = new THREE.Quaternion(chassisRotation.x, chassisRotation.y, chassisRotation.z, chassisRotation.w);
    const forwardDirection = new THREE.Vector3(0, 0, -1).applyQuaternion(chassisQuaternion);

    // Calculate forward speed by projecting velocity onto forward direction
    const forwardSpeed = velocityVector.dot(forwardDirection);

    // Update each wheel's rotation
    for (let i = 0; i < this.vehicleController.numWheels(); i++) {
      const wheelRadius = this.vehicleController.wheelRadius(i);
      if (wheelRadius !== null && wheelRadius > 0) {
        // Calculate angular velocity: v = ωr, so ω = v/r
        const angularVelocity = forwardSpeed / wheelRadius;

        // Update wheel rotation angle
        const currentRotation = this.wheelRotationAngles.get(i) ?? 0;
        const newRotation = currentRotation + angularVelocity * deltaTime;
        this.wheelRotationAngles.set(i, newRotation);
      }
    }
  }

  public addWheel(wheelConfig: VehicleWheelConfig): number {
    if (!this.vehicleController) {
      return -1;
    }
    const wheelIndex = this.vehicleController.numWheels();

    // Add wheel to Rapier vehicle controller
    this.vehicleController.addWheel(
      wheelConfig.position,
      wheelConfig.wheelDirection ?? RapierVehicle.DEFAULT_DIRECTION,
      wheelConfig.wheelAxle ?? RapierVehicle.DEFAULT_AXLE,
      wheelConfig.suspensionRestLength,
      wheelConfig.radius
    );

    this.wheelConfigs.set(wheelIndex, wheelConfig);
    // Initialize wheel rotation angle
    this.wheelRotationAngles.set(wheelIndex, 0);

    if (wheelConfig.suspensionStiffness) {
      this.vehicleController.setWheelSuspensionStiffness(wheelIndex, wheelConfig.suspensionStiffness);
    }

    if (wheelConfig.suspensionCompression) {
      this.vehicleController.setWheelSuspensionCompression(wheelIndex, wheelConfig.suspensionCompression);
    }

    if (wheelConfig.suspensionRelaxation) {
      this.vehicleController.setWheelSuspensionRelaxation(wheelIndex, wheelConfig.suspensionRelaxation);
    }

    if (wheelConfig.maxSuspensionForce) {
      this.vehicleController.setWheelMaxSuspensionForce(wheelIndex, wheelConfig.maxSuspensionForce);
    }

    if (wheelConfig.maxSuspensionTravel) {
      this.vehicleController.setWheelMaxSuspensionTravel(wheelIndex, wheelConfig.maxSuspensionTravel);
    }

    if (wheelConfig.sideFrictionStiffness) {
      this.vehicleController.setWheelSideFrictionStiffness(wheelIndex, wheelConfig.sideFrictionStiffness);
    }

    if (wheelConfig.frictionSlip) {
      this.vehicleController.setWheelFrictionSlip(wheelIndex, wheelConfig.frictionSlip);
    }

    // return the wheel index
    return wheelIndex;
  }

  public setEngineForce(force: number): void {
    if (!this.vehicleController) {
      return;
    }

    for (const wheelIndex of this.wheelConfigs.keys()) {
      const wheel = this.wheelConfigs.get(wheelIndex);
      if (wheel && wheel.isPowered) {
        this.vehicleController!.setWheelEngineForce(wheelIndex, force);
      }
    }
  }

  public setBrakeForce(force: number): void {
    if (!this.vehicleController) {
      return;
    }

    for (const wheelIndex of this.wheelConfigs.keys()) {
      const wheel = this.wheelConfigs.get(wheelIndex);
      if (wheel && wheel.canBrake) {
        this.vehicleController.setWheelBrake(wheelIndex, force);
      }
    }
  }

  public setSteering(angle: number): void {
    if (!this.vehicleController) {
      return;
    }

    for (const wheelIndex of this.wheelConfigs.keys()) {
      const wheel = this.wheelConfigs.get(wheelIndex);
      if (wheel && wheel.canSteer) {
        this.vehicleController.setWheelSteering(wheelIndex, angle);
      }
    }
  }

  public numWheels(): number {
    return this.vehicleController?.numWheels() ?? 0;
  }

  public getWheelState(wheelIndex: number): VehicleWheelState | null {
    if (!this.vehicleController || wheelIndex < 0 || wheelIndex >= this.vehicleController.numWheels()) {
      return null;
    }

    // Get wheel info from Rapier
    const suspensionLength = this.vehicleController.wheelSuspensionLength(wheelIndex) ?? 0;
    const steeringAngle = this.vehicleController.wheelSteering(wheelIndex) ?? 0;
    // Use our calculated rotation angle instead of the unreliable wheelRotation
    const rotationAngle = this.wheelRotationAngles.get(wheelIndex) ?? 0;

    const wheelAxleCs = this.vehicleController.wheelAxleCs(wheelIndex) ?? RapierVehicle.DEFAULT_AXLE;
    const wheelDirectionCs = this.vehicleController.wheelDirectionCs(wheelIndex) ?? RapierVehicle.DEFAULT_DIRECTION;
    const connectionPoint = this.vehicleController.wheelChassisConnectionPointCs(wheelIndex) ?? RapierVehicle.ZERO_VECTOR;

    const relativePosition = {
      x: connectionPoint.x + wheelDirectionCs.x * suspensionLength,
      y: connectionPoint.y + wheelDirectionCs.y * suspensionLength,
      z: connectionPoint.z + wheelDirectionCs.z * suspensionLength,
    };

    const wheelSteeringQuat = new THREE.Quaternion();
    const wheelRotationQuat = new THREE.Quaternion();
    wheelSteeringQuat.setFromAxisAngle(wheelDirectionCs, -steeringAngle);
    wheelRotationQuat.setFromAxisAngle(wheelAxleCs, rotationAngle);

    const contactNormal = this.vehicleController.wheelContactNormal(wheelIndex);
    const contactPoint = this.vehicleController.wheelContactPoint(wheelIndex);
    const hardPoint = this.vehicleController.wheelHardPoint(wheelIndex);

    return {
      relativePosition: new THREE.Vector3(relativePosition.x, relativePosition.y, relativePosition.z),
      relativeQuaternion: new THREE.Quaternion().multiplyQuaternions(wheelSteeringQuat, wheelRotationQuat),
      steeringAngle,
      rotationAngle,
      suspensionRestLength: this.vehicleController.wheelSuspensionRestLength(wheelIndex),
      maxSuspensionTravel: this.vehicleController.wheelMaxSuspensionTravel(wheelIndex),
      radius: this.vehicleController.wheelRadius(wheelIndex),
      suspensionStiffness: this.vehicleController.wheelSuspensionStiffness(wheelIndex),
      suspensionCompression: this.vehicleController.wheelSuspensionCompression(wheelIndex),
      suspensionRelaxation: this.vehicleController.wheelSuspensionRelaxation(wheelIndex),
      maxSuspensionForce: this.vehicleController.wheelMaxSuspensionForce(wheelIndex),
      brakeForce: this.vehicleController.wheelBrake(wheelIndex),
      engineForce: this.vehicleController.wheelEngineForce(wheelIndex),
      frictionSlip: this.vehicleController.wheelFrictionSlip(wheelIndex),
      sideFrictionStiffness: this.vehicleController.wheelSideFrictionStiffness(wheelIndex),
      suspensionForce: this.vehicleController.wheelSuspensionForce(wheelIndex),
      contactNormal: contactNormal ? new THREE.Vector3(contactNormal.x, contactNormal.y, contactNormal.z) : null,
      contactPoint: contactPoint ? new THREE.Vector3(contactPoint.x, contactPoint.y, contactPoint.z) : null,
      suspensionLength: this.vehicleController.wheelSuspensionLength(wheelIndex),
      hardPoint: hardPoint ? new THREE.Vector3(hardPoint.x, hardPoint.y, hardPoint.z) : null,
      isInContact: this.vehicleController.wheelIsInContact(wheelIndex) ?? null,
    };
  }

  public getState(): VehicleState | null {
    if (!this.vehicleController) {
      return null;
    }
    const chassisBody = this.options.chassis;
    const velocity = chassisBody.linvel();
    const angularVelocity = chassisBody.angvel();

    const velocityVec = new THREE.Vector3(velocity.x, velocity.y, velocity.z);
    const speed = velocityVec.length();

    // Count wheels on ground
    let wheelsOnGround = 0;
    for (let i = 0; i < this.vehicleController.numWheels(); i++) {
      if (this.vehicleController.wheelIsInContact(i)) {
        wheelsOnGround++;
      }
    }

    return {
      speed: speed,
      velocity: velocityVec,
      angularVelocity: new THREE.Vector3(angularVelocity.x, angularVelocity.y, angularVelocity.z),
      wheelsOnGround: wheelsOnGround,
      totalWheels: this.vehicleController.numWheels(),
    };
  }
}
